import { xml2json } from '@app/cuss/embross/utils/helper';
import { DeviceManager } from 'src/embross-device-manager';
import OosManager from '@app/cuss/embross/devices/osManager';
import {
  CussStatusCode,
  CussStatusLabel,
  ConveyorStatus,
} from '@app/enums/cuss-device-status.enum';

/**
 * Shared context object passed to device callbacks.
 * Provides access to DeviceManager, OOS Manager, and emergency service.
 * The emergency service is optional — when not provided, emergency code
 * handling falls back to the existing log-only behavior.
 */
export interface CallbackContext {
  getDeviceManager(): DeviceManager;
  getOOSManager(): OosManager;
  /** Optional: Angular service for emergency modal lifecycle. */
  getSsbdEmergencyService?(): {
    handleUnsolicitedMessage(raw: string): boolean;
    handleStatusChange(statusCode: number): boolean;
    handleStatusIsOK(value: string): boolean;
  } | null;
  /**
   * Optional: Angular service for the Bag Tag paper episode
   * (CI-22506 low paper, CVT20-14314 empty paper).
   */
  getBagtagMediaLowService?(): {
    notifyMediaLow(): void;
    notifyMediaEmpty(): void;
    notifyMediaRecovered(): void;
  } | null;
  /**
   * Optional: Angular service for the Boarding Pass (ATB) printer paper
   * condition (CI-23161 low paper / empty paper).
   */
  getBoardingPassPaperService?(): {
    notifyMediaLow(): void;
    notifyMediaEmpty(): void;
    notifyMediaRecovered(): void;
  } | null;
}

//#region Scale (BAGGAGE_SCALE)

/**
 * Creates the callback for the BAGGAGE_SCALE device.
 */
export function createScaleCallback(ctx: CallbackContext) {
  return (event: any) => {
    switch (event.key) {
      case 'statusResponse':
        console.warn('[SCALE] statusResponse:', event.value);
        break;

      case 'baggagePresent':
        console.warn('[SCALE] baggagePresent');
        break;

      case 'baggageReadInternal':
      case 'baggageAbsent': {
        const response = event.value;
        const xmlNode = new DOMParser().parseFromString(response, 'text/xml');
        const dataRead = JSON.parse(xml2json(xmlNode));
        let weight = 0;

        if (event.key === 'baggageReadInternal' && dataRead.baggageData?.dimension) {
          weight = dataRead.baggageData.dimension.load.weight / 1000;
          weight = Math.floor(weight);
        }

        console.warn(`[SCALE] ${event.key}: weight=${weight}kg`);
        break;
      }

      case 'statusChange': {
        const statusCode = Array.isArray(event.value) ? event.value[0] : event.value;
        const label = CussStatusLabel[statusCode] || 'UNKNOWN';
        console.warn(`[SCALE] statusChange: code=${statusCode} (${label})`);

        if (statusCode > 300 && statusCode < 311) {
          console.error(`[SCALE] CRITICAL ERROR: ${label} (code: ${statusCode})`);
          ctx
            .getDeviceManager()
            .getAppManager()
            .sendApplicationLog(100, `CDS_APPLOG,${statusCode},scale ${label}`);
          ctx.getOOSManager().completeTransaction(true);
        }
        break;
      }

      default:
        console.warn('[SCALE] event:', event.key, event.value);
        break;
    }
  };
}

//#endregion

//#region SSBD Conveyor/Belt (AEA_BAGDROP)

/**
 * Creates the callback for the AEA_BAGDROP (SSBD conveyor/belt) device.
 * Detects: NOT_RESPONDING, HARDWARE_ERROR, MEDIA_JAMMED, EMERGENCY, INTRUSION, JAM, FAULT.
 */
export function createSsbdCallback(ctx: CallbackContext) {
  return (event: any) => {
    switch (event.key) {
      case 'statusChange':
        handleSsbdStatusChange(event.value, ctx);
        break;

      case 'statusIsOK': {
        console.warn('[SSBD] statusIsOK:', event.value);
        // Check for emergency codes in statusIsOK (e.g. "302,0")
        const emergencySvcOK = ctx.getSsbdEmergencyService?.();
        if (emergencySvcOK) {
          emergencySvcOK.handleStatusIsOK(event.value);
        }
        break;
      }

      case 'unsolicitedMessage':
        handleSsbdUnsolicitedMessage(event.value, ctx);
        break;

      case 'commandCompleted':
        console.warn('[SSBD] commandCompleted:', event.value);
        break;

      default:
        console.warn('[SSBD] event:', event.key, event.value);
        break;
    }
  };
}

/**
 * Handles statusChange events from the SSBD conveyor.
 * Emergency codes 139/302/305 are delegated to SsbdEmergencyService which handles
 * the modal lifecycle and threshold logic. The service returns `true` for these codes,
 * so they never reach the generic 301-310 OOS block below.
 *
 * Status codes 301, 303, 304, 306-310 are critical hardware failures -> OOS.
 * Status code 101 = MEDIA_JAMMED (belt jammed).
 */
function handleSsbdStatusChange(value: any, ctx: CallbackContext): void {
  const statusCode = Array.isArray(value) ? value[0] : value;
  const label = CussStatusLabel[statusCode] || 'UNKNOWN';

  console.warn(`[SSBD] statusChange: code=${statusCode} (${label})`);

  // Delegate to emergency service for codes 139/302/305/122
  const emergencySvc = ctx.getSsbdEmergencyService?.();
  if (emergencySvc) {
    const handled = emergencySvc.handleStatusChange(statusCode);
    if (handled) {
      // Emergency service handled the code (modal lifecycle + threshold for 302).
      // Important: returning here prevents 302 from falling into the 301-310 OOS block.
      return;
    }
  }

  // Critical hardware errors (301-310) -> go Out of Service
  // Note: 302 and 305 are excluded when emergency service is available (handled above).
  // When emergency service is NOT available (shouldn't happen in production), 302/305
  // still fall through here as a safety net.
  if (statusCode > 300 && statusCode < 311) {
    // Defense in depth: skip 302 and 305 even without emergency service
    if (
      statusCode === CussStatusCode.HARDWARE_ERROR ||
      statusCode === CussStatusCode.NOT_RESPONDING
    ) {
      console.warn(`[SSBD] code ${statusCode} without emergency service — skipping generic OOS`);
      return;
    }
    console.error(`[SSBD] CRITICAL ERROR: ${label} (code: ${statusCode})`);
    ctx
      .getDeviceManager()
      .getAppManager()
      .sendApplicationLog(100, `CDS_APPLOG,${statusCode},ssbd ${label}`);
    ctx.getOOSManager().completeTransaction(true);
    return;
  }

  // Belt jammed
  if (statusCode === CussStatusCode.MEDIA_JAMMED) {
    console.error('[SSBD] BELT JAMMED (MEDIA_JAMMED: 101)');
    ctx
      .getDeviceManager()
      .getAppManager()
      .sendApplicationLog(100, `CDS_APPLOG,${statusCode},ssbd MEDIA_JAMMED`);
    return;
  }

  // Timeout
  if (statusCode === CussStatusCode.TIMEOUT) {
    console.warn('[SSBD] TIMEOUT on ssbd command');
    return;
  }

  // Software error
  if (statusCode === CussStatusCode.SOFTWARE_ERROR) {
    console.warn('[SSBD] SOFTWARE_ERROR on ssbd');
    return;
  }
}

/**
 * Handles unsolicited (async) messages from the SSBD conveyor.
 * Delegates emergency code handling (139/302/122) to SsbdEmergencyService when available.
 * Falls back to existing EMCY/INTR/JAM/FALT string detection for non-numeric codes.
 */
function handleSsbdUnsolicitedMessage(value: string, ctx: CallbackContext): void {
  console.warn('[SSBD] unsolicitedMessage raw:', value);

  // Delegate numeric emergency codes (139/302/122) to the emergency service
  const emergencySvc = ctx.getSsbdEmergencyService?.();
  if (emergencySvc) {
    const handled = emergencySvc.handleUnsolicitedMessage(value);
    if (handled) {
      // Emergency service already handled modal + OOS for 302; skip further processing
      return;
    }
  }

  const params = value.split('#');
  let statusDevice = '';
  let bagStatus = '';

  params.forEach((param) => {
    const [key, val] = param.split('=');
    if (key === 'SD') statusDevice = val || '';
    if (key === 'BS') bagStatus = val || '';
  });

  // Emergency button pressed (string-based legacy detection)
  if (statusDevice === ConveyorStatus.EMERGENCY || bagStatus === ConveyorStatus.EMERGENCY) {
    console.error('[SSBD] EMERGENCY BUTTON PRESSED (EMCY)');
    ctx
      .getDeviceManager()
      .getAppManager()
      .sendApplicationLog(100, 'CDS_APPLOG,EMCY,ssbd emergency stop activated');
    return;
  }

  // Intrusion detected
  if (statusDevice === ConveyorStatus.INTRUSION || bagStatus === ConveyorStatus.INTRUSION) {
    console.error('[SSBD] INTRUSION DETECTED (INTR)');
    ctx
      .getDeviceManager()
      .getAppManager()
      .sendApplicationLog(100, 'CDS_APPLOG,INTR,ssbd intrusion detected');
    return;
  }

  // Belt jammed (via AEA message)
  if (statusDevice === ConveyorStatus.JAMMED || bagStatus === ConveyorStatus.JAMMED) {
    console.error('[SSBD] BELT JAMMED (JAM)');
    ctx
      .getDeviceManager()
      .getAppManager()
      .sendApplicationLog(100, 'CDS_APPLOG,JAM,ssbd belt jammed');
    return;
  }

  // Fault
  if (statusDevice === ConveyorStatus.FAULT || bagStatus === ConveyorStatus.FAULT) {
    console.error('[SSBD] BELT FAULT (FALT)');
    ctx
      .getDeviceManager()
      .getAppManager()
      .sendApplicationLog(100, 'CDS_APPLOG,FALT,ssbd fault detected');
    return;
  }

  // Normal operation log
  if (statusDevice || bagStatus) {
    console.warn(`[SSBD] SD=${statusDevice}, BS=${bagStatus}`);
  }
}

//#endregion
